// Which local planning authority a point is in, from planning.data.gov.uk's public boundaries (it allows calls from the
// browser). Only the authority's name: knowing it gives nothing of the assessment away (see CLAUDE.md, no free information).

const ENDPOINT = 'https://www.planning.data.gov.uk/entity.json';

interface Entity { name?: string; 'end-date'?: string | null }

/** The tidy name of the authority covering the point, e.g. "Bristol" for "Bristol, City of LPA"; null when none is found. */
export async function lookUpPlanningAuthority(lat: number, lon: number): Promise<string | null> {
  const url = `${ENDPOINT}?dataset=local-planning-authority&latitude=${lat}&longitude=${lon}&limit=5`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`The local planning authority lookup is unavailable (HTTP ${r.status})`);
  const entities = ((await r.json()) as { entities?: Entity[] }).entities ?? [];
  const current = entities.filter(e => e.name && !e['end-date']);
  // A National Park authority is the planning authority inside its park, so it wins over the district it overlaps.
  const chosen = current.find(e => /national park/i.test(e.name!)) ?? current[0];
  return chosen?.name ? inFormerArea(tidyAuthorityName(chosen.name), lat, lon) : null;
}

/**
 * Authorities where different plans still apply across former districts, so policy is held per area. Each district's name in
 * planning.data's local-authority-district dataset maps to the name used in the policy library.
 * Somerset's plans follow the districts before 2019, so Taunton Deane and West Somerset are separate. Dorset's West Dorset
 * and Weymouth and Portland share one plan, so both districts map to one area.
 */
export const FORMER_AREAS: Record<string, Record<string, string>> = {
  Dorset: {
    'East Dorset': 'Dorset (former East Dorset area)',
    'North Dorset': 'Dorset (former North Dorset area)',
    Purbeck: 'Dorset (former Purbeck area)',
    'West Dorset': 'Dorset (former West Dorset, Weymouth and Portland area)',
    'Weymouth and Portland': 'Dorset (former West Dorset, Weymouth and Portland area)',
  },
  Somerset: {
    Mendip: 'Somerset (former Mendip area)',
    Sedgemoor: 'Somerset (former Sedgemoor area)',
    'South Somerset': 'Somerset (former South Somerset area)',
    'Taunton Deane': 'Somerset (former Taunton Deane area)',
    'West Somerset': 'Somerset (former West Somerset area)',
  },
};

/** The former area covering the point. With none found it stays the whole authority, which has no policy of its own: never a guess. */
async function inFormerArea(authority: string, lat: number, lon: number): Promise<string> {
  const areas = FORMER_AREAS[authority];
  if (!areas) return authority;
  const r = await fetch(`${ENDPOINT}?dataset=local-authority-district&latitude=${lat}&longitude=${lon}&limit=10`);
  if (!r.ok) throw new Error(`The local planning authority lookup is unavailable (HTTP ${r.status})`);
  const districts = ((await r.json()) as { entities?: Entity[] }).entities ?? [];
  const district = districts.find(d => d.name && areas[d.name])?.name;
  return district ? areas[district] : authority;
}

export interface CoverageBoundaries { type: 'FeatureCollection'; features: unknown[] }

let authorityIndex: Promise<{ entity: number; name: string }[]> | null = null;
const boundaryCache = new Map<string, Promise<CoverageBoundaries>>();

/** Every current local planning authority's planning.data entity number and name; fetched once per visit. */
function currentAuthorities(): Promise<{ entity: number; name: string }[]> {
  authorityIndex ??= fetch(`${ENDPOINT}?dataset=local-planning-authority&limit=500&field=entity&field=name&field=end-date`)
    .then(async r => {
      if (!r.ok) throw new Error(`The local planning authority list is unavailable (HTTP ${r.status})`);
      const rows = ((await r.json()) as { entities?: (Entity & { entity?: number })[] }).entities ?? [];
      return rows.filter(e => e.name && typeof e.entity === 'number' && !e['end-date']).map(e => ({ entity: e.entity!, name: e.name! }));
    })
    .catch(e => { authorityIndex = null; throw e; });
  return authorityIndex;
}

/**
 * The boundaries of the local planning authorities we cover (named as in the policy library), from planning.data, to shade
 * on a map. Grows on its own as authorities are added to the library. Cached for the visit.
 */
export function coveredAuthorityBoundaries(ours: string[]): Promise<CoverageBoundaries> {
  const cacheKey = [...ours].sort().join('|');
  let cached = boundaryCache.get(cacheKey);
  if (!cached) {
    cached = (async () => {
      const ids = (await currentAuthorities()).filter(a => matchAuthority(tidyAuthorityName(a.name), ours)).map(a => a.entity);
      const features: unknown[] = ids.length ? await boundaryFeatures('local-planning-authority', ids) : [];
      return { type: 'FeatureCollection' as const, features: [...features, ...await formerAreaBoundaries(ours)] };
    })();
    cached.catch(() => boundaryCache.delete(cacheKey));
    boundaryCache.set(cacheKey, cached);
  }
  return cached;
}

async function boundaryFeatures(dataset: string, ids: number[]): Promise<unknown[]> {
  const r = await fetch(`https://www.planning.data.gov.uk/entity.geojson?dataset=${dataset}&${ids.map(id => `entity=${id}`).join('&')}&limit=${ids.length}`);
  if (!r.ok) throw new Error(`The local planning authority boundaries are unavailable (HTTP ${r.status})`);
  return ((await r.json()) as { features?: unknown[] }).features ?? [];
}

/** The covered former areas' boundaries, named as in the policy library. */
async function formerAreaBoundaries(ours: string[]): Promise<unknown[]> {
  const labelByDistrict = new Map(Object.values(FORMER_AREAS).flatMap(areas => Object.entries(areas))
    .filter(([, label]) => matchAuthority(label, ours)));
  if (!labelByDistrict.size) return [];
  const list = await fetch(`${ENDPOINT}?dataset=local-authority-district&limit=500&field=entity&field=name`);
  if (!list.ok) throw new Error(`The local planning authority boundaries are unavailable (HTTP ${list.status})`);
  const rows = ((await list.json()) as { entities?: (Entity & { entity?: number })[] }).entities ?? [];
  const ids = rows.filter(e => e.name && labelByDistrict.has(e.name) && typeof e.entity === 'number').map(e => e.entity!);
  if (!ids.length) return [];
  const features = await boundaryFeatures('local-authority-district', ids) as { properties?: { name?: string } }[];
  return features.map(f => ({ ...f, properties: { ...f.properties, name: labelByDistrict.get(f.properties?.name ?? '') ?? f.properties?.name } }));
}

/** Outline points checked besides the site's own point; enough to catch a site that crosses into a neighbouring authority. */
const MAX_OUTLINE_POINTS = 12;

/**
 * The authority the site is in (at its point) and any other authority its outline reaches, so a site that straddles a boundary
 * can be flagged. Without an outline only the point is checked. A failed outline point is skipped; a failed site point throws.
 */
export async function lookUpSiteAuthorities(site: { lat: number; lon: number; boundary?: { ring?: [number, number][] } }): Promise<{ name: string | null; others: string[] }> {
  const ring = Array.isArray(site.boundary?.ring) ? site.boundary!.ring!.slice(0, -1) : [];
  const step = Math.max(1, Math.ceil(ring.length / MAX_OUTLINE_POINTS));
  const outline = ring.filter((_, i) => i % step === 0).slice(0, MAX_OUTLINE_POINTS);
  const [name, ...edge] = await Promise.all([
    lookUpPlanningAuthority(site.lat, site.lon),
    ...outline.map(([lon, lat]) => lookUpPlanningAuthority(lat, lon).catch(() => null)),
  ]);
  const others = [...new Set(edge.filter((n): n is string => !!n && n !== name))];
  return { name, others };
}

export function tidyAuthorityName(name: string): string {
  return name
    .replace(/\s+LPA$/i, '')
    .replace(/,\s*(City|County|Royal Borough|Borough) of$/i, '')
    .replace(/^(City|County|Royal Borough|Borough) of\s+/i, '')
    .trim();
}

const key = (s: string) => s.toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/\b(council|city|county|borough|district|metropolitan|royal|of|the|lpa)\b/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/** The one of our authorities (as named in the policy library) that is the same as `name`, if any. */
export function matchAuthority(name: string, ours: string[]): string | null {
  const want = key(name);
  return ours.find(o => key(o) === want) ?? null;
}
